/**
 * Registers global error handling hooks - and provides functions to report errors
 * back to error reporting services.
 */

import { BrewManApiError } from "@/app/errors/index";
import eventLogger from "@/app/eventlogging/eventLogger";
import appStore from "@/store/modules/appStore";

// Indexed Database: Currently causing log spam
// Failed to fetch: Bad connectivity issues are local issues
const ignoreErrorPatterns = [
  "Indexed Database",
  "Failed to fetch",
  "indexedDB",
  "IDB",
  "The current transaction exceeded its quota limitations",
];

// Global unhandled rejection JS errors that shouldn't be shown to the user
// or logged
const jsErrorsToSuppress = [
  "The play() request was interrupted",
  "NavigationDuplicated: Avoided redundant navigation to current location:",
];

interface LogMetadata {
  [key: string]: any;
}

// Errors caught outside of vue still need to be captured
// Add a button element to the index.html with an onclick to test this gets caught
window.addEventListener("error", (ev) => {
  logError("global error", ev.error);
});

// Any unhandled promises are thrown here IE: async DOM events
// This notably happened on the buttonbar buttons with async click events
window.addEventListener("unhandledrejection", (ev) => {
  if (ev.reason instanceof Error) {
    if (jsErrorsToSuppress.some((e) => ev.reason.toString().includes(e))) {
      return;
    }

    logError("Unhandled promise rejection", ev.reason);
    appStore.showError(ev.reason);
    return;
  }

  const error = new Error();
  error.name = ev.type;

  // We might get a stack as the reason
  if (ev.reason.stack) {
    error.stack = ev.reason.stack;
  } else {
    error.message = ev.reason;
  }

  logError("Unhandled promise rejection", error);
  appStore.showError(error);
});

function isError(error: any): error is Error {
  return error instanceof Error;
}

/* eslint-disable no-console */
function logError(
  error: string,
  errorDetails?: Error | string,
  additionalContext?: string
) {
  // No error information - nothing to do
  if (!error) {
    return;
  }

  // allow grouping of BrewmanApiError
  const brewmanApiErrorMessage: string | null =
    errorDetails instanceof BrewManApiError
      ? `BrewmanApiError: ${
          errorDetails.reasonCode ? errorDetails.reasonCode + "," : ""
        }${errorDetails.message}`
      : null;
  const errorTitle =
    brewmanApiErrorMessage || (error?.toString() ?? "UnknownError");

  const errorMessage = isError(errorDetails)
    ? errorDetails.message
    : (errorDetails ?? "");

  // Ignore IDB errors for now
  const shouldIgnoreLoggingError = ignoreErrorPatterns.some((ignorePattern) =>
    errorMessage.includes(ignorePattern)
  );

  if (shouldIgnoreLoggingError) {
    return;
  }

  const eventProperties = isError(errorDetails)
    ? {
        severity: "ERROR",
        errorName: errorTitle,
        errorMessage: errorDetails?.message,
        additionalContext,
        stackTrace: errorDetails?.stack,
      }
    : {
        severity: "ERROR",
        errorName: errorTitle,
        errorMessage: errorDetails,
        additionalContext,
      };

  // Log errors out to event logger too
  eventLogger.logEvent("brewman_js_error", eventProperties);
  return eventProperties;
}

function logInfo(message: string, metadata?: LogMetadata) {
  eventLogger.logEvent("brewman_js_info", {
    severity: "INFO",
    infoMessage: message,
    ...metadata,
  });
}

export default {
  logError,
  logInfo,
};
